import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useI18n } from '../../i18n';
import { api } from '../../services/api';
import { useAuth } from './AuthContext';
import AuthPromptDialog from './AuthPromptDialog';
import Contact from './Contact';
import CookieStorage from './CookieStorage';
import LegalLanguageMenu from './LegalLanguageMenu';
import Privacy from './Privacy';
import ShareToastHost from './ShareToastHost';
import Terms from './Terms';
import { setPageMeta } from './pageMeta';
import useModalEscape from './useModalEscape';
import useSiteVisitTracking from './useSiteVisitTracking';

function avatarImageUrl(url) {
  if (!url) return '';
  const value = String(url);
  if (value.startsWith('/media/') || value.startsWith('/outfit-detective/') || value.includes('/outfit-detective/')) return value;
  return `/api/media/avatar?url=${encodeURIComponent(url)}`;
}

function normalizeSearchText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

const footerLegalItems = [
  { key: 'privacy', titleKey: 'login.legalLink' },
  { key: 'terms', titleKey: 'login.termsLink' },
  { key: 'contact', titleKey: 'legal.contactTitle' },
  { key: 'legal', titleKey: 'app.legal' },
];

const CelebLayout = () => {
  const {
    loading,
    isAuthenticated,
    showNewUserWelcome,
    followRefreshPrompt,
    dismissNewUserWelcome,
    dismissFollowRefreshPrompt,
    logout,
    requestLogin,
  } = useAuth();
  const { language, languages, setLanguage, t } = useI18n();
  const currentLanguage = languages.find((item) => item.code === language) || languages[0];
  const [languageOpen, setLanguageOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileFollowsOpen, setMobileFollowsOpen] = useState(false);
  const [mobileFollows, setMobileFollows] = useState([]);
  const [mobileFollowsSearch, setMobileFollowsSearch] = useState('');
  const [mobileFollowsLoading, setMobileFollowsLoading] = useState(false);
  const [mobileFollowsError, setMobileFollowsError] = useState('');
  const [followRefreshStatus, setFollowRefreshStatus] = useState('');
  const [followRefreshLoading, setFollowRefreshLoading] = useState(false);
  const [activeLegal, setActiveLegal] = useState('');
  const [unreadAlerts, setUnreadAlerts] = useState(0);
  const languageRef = useRef(null);
  const userMenuRef = useRef(null);
  const mobileFollowsDragRef = useRef({ active: false, startY: 0, currentY: 0 });
  const navigate = useNavigate();
  const location = useLocation();
  useSiteVisitTracking(location, language);
  useModalEscape(mobileFollowsOpen, () => setMobileFollowsOpen(false));
  useModalEscape(showNewUserWelcome, dismissNewUserWelcome);
  useModalEscape(Boolean(followRefreshPrompt), dismissFollowRefreshPrompt);
  useModalEscape(Boolean(activeLegal), () => setActiveLegal(''));
  const filteredMobileFollows = useMemo(() => {
    const query = normalizeSearchText(mobileFollowsSearch);
    if (!query) return mobileFollows;
    return mobileFollows.filter((item) => normalizeSearchText([item.name, item.slug].filter(Boolean).join(' ')).includes(query));
  }, [mobileFollows, mobileFollowsSearch]);

  useEffect(() => {
    function handlePointerDown(event) {
      if (!languageRef.current?.contains(event.target)) setLanguageOpen(false);
      if (!userMenuRef.current?.contains(event.target)) setUserMenuOpen(false);
    }

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, []);

  useEffect(() => {
    if (!activeLegal) return undefined;
    document.body.classList.add('ct-modal-open');
    return () => {
      document.body.classList.remove('ct-modal-open');
    };
  }, [activeLegal]);

  useEffect(() => {
    const pathname = location.pathname.replace(/\/+$/, '') || '/';
    const tab = new URLSearchParams(location.search).get('tab');
    const titleFor = (label) => `${label} | Outfit Detective`;
    const routeMeta = {
      '/': {
        title: titleFor(t('app.feed')),
        description: t('feed.heroTitle'),
      },
      '/fit-wall': {
        title: titleFor(t('tryonFeed.title')),
        description: t('tryonFeed.copy'),
      },
      '/how-it-works': {
        title: titleFor(t('how.metaTitle')),
        description: t('how.metaDescription'),
      },
      '/discover': {
        title: titleFor(t('app.discover')),
        description: t('discover.title'),
      },
      '/photos': {
        title: titleFor(t('photos.title')),
        description: t('photos.copy'),
        robots: 'noindex,follow',
      },
      '/saved': {
        title: titleFor(t('saved.title')),
        description: t('saved.emptyCopy'),
        robots: 'noindex,follow',
      },
      '/profile': {
        title: titleFor(tab === 'tryons' ? t('tryonGallery.title') : tab === 'photos' ? t('photos.title') : tab === 'alerts' ? t('alerts.tab') : t('account.title')),
        description: t('account.title'),
        robots: 'noindex,follow',
      },
    };

    const meta = routeMeta[pathname];
    if (!meta) return;
    setPageMeta({
      pathname,
      robots: meta.robots || 'index,follow',
      ...meta,
    });
  }, [location.pathname, location.search, t]);

  useEffect(() => {
    let mounted = true;

    async function loadUnreadAlerts() {
      if (!isAuthenticated) {
        setUnreadAlerts(0);
        return;
      }
      try {
        const response = await api.getUserNotificationSummary();
        if (mounted) setUnreadAlerts(Number(response.data?.unread_count || 0));
      } catch {
        if (mounted) setUnreadAlerts(0);
      }
    }

    loadUnreadAlerts();
    window.addEventListener('od:notifications-refresh', loadUnreadAlerts);
    return () => {
      mounted = false;
      window.removeEventListener('od:notifications-refresh', loadUnreadAlerts);
    };
  }, [isAuthenticated, location.pathname]);

  function goToFeed(event) {
    event.preventDefault();
    window.dispatchEvent(new CustomEvent('od:feed-refresh'));
    if (location.pathname === '/') return;
    navigate('/');
  }

  async function loadMobileFollows() {
    setMobileFollowsLoading(true);
    setMobileFollowsError('');
    try {
      const response = await api.getFollows();
      setMobileFollows(response.data || []);
    } catch (err) {
      setMobileFollowsError(err.message || 'request_failed');
    } finally {
      setMobileFollowsLoading(false);
    }
  }

  async function openMobileFollows() {
    setUserMenuOpen(false);
    if (!isAuthenticated) {
      requestLogin({
        title: t('authPrompt.followTitle'),
        copy: t('authPrompt.followCopy'),
        afterLogin: openMobileFollows,
      });
      return;
    }
    setMobileFollowsSearch('');
    setMobileFollowsOpen(true);
    await loadMobileFollows();
  }

  function requireLoginFor(event, options = {}) {
    if (isAuthenticated) return false;
    event.preventDefault();
    setUserMenuOpen(false);
    requestLogin(options);
    return true;
  }

  async function removeMobileFollow(id) {
    try {
      await api.unfollowCelebrity(id);
      setMobileFollows((current) => current.filter((item) => Number(item.id) !== Number(id)));
      window.dispatchEvent(new CustomEvent('od:feed-refresh'));
    } catch (err) {
      setMobileFollowsError(err.message || 'request_failed');
    }
  }

  async function refreshFollowedFeeds() {
    setFollowRefreshLoading(true);
    setFollowRefreshStatus('');
    try {
      const response = await api.refreshFollowedFeeds();
      setFollowRefreshStatus(
        response.refresh_started > 0
          ? t('followRefresh.success', { count: response.refresh_started })
          : t('followRefresh.fresh'),
      );
      window.dispatchEvent(new CustomEvent('od:feed-refresh'));
      window.setTimeout(dismissFollowRefreshPrompt, 1600);
    } catch (err) {
      setFollowRefreshStatus(t('followRefresh.error'));
    } finally {
      setFollowRefreshLoading(false);
    }
  }

  function startMobileFollowsDrag(event) {
    if (event.target.closest('a, button, input, select, textarea')) return;
    if (!event.target.closest('.ct-sheet-drag-zone, .ct-panel-heading')) return;
    const clientY = event.touches?.[0]?.clientY ?? event.clientY;
    mobileFollowsDragRef.current = { active: true, startY: clientY, currentY: 0 };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }

  function moveMobileFollowsDrag(event) {
    if (!mobileFollowsDragRef.current.active) return;
    const clientY = event.touches?.[0]?.clientY ?? event.clientY;
    const distance = Math.max(0, clientY - mobileFollowsDragRef.current.startY);
    mobileFollowsDragRef.current.currentY = distance;
    event.currentTarget.style.transform = `translateY(${distance}px)`;
  }

  function endMobileFollowsDrag(event) {
    if (!mobileFollowsDragRef.current.active) return;
    const distance = mobileFollowsDragRef.current.currentY;
    mobileFollowsDragRef.current = { active: false, startY: 0, currentY: 0 };
    event.currentTarget.style.transform = '';
    if (distance > 96) setMobileFollowsOpen(false);
  }

  function renderLegalDocument() {
    if (activeLegal === 'privacy') return <Privacy compact />;
    if (activeLegal === 'terms') return <Terms compact />;
    if (activeLegal === 'contact') return <Contact compact />;
    if (activeLegal === 'cookies') return <CookieStorage compact />;
    if (activeLegal === 'legal') {
      return (
        <main className="ct-legal-main">
          <article className="ct-legal-doc">
            <div className="ct-legal-head">
              <p className="ct-legal-kicker">{t('legal.kicker')}</p>
              <h1>{t('legal.title')}</h1>
              <p className="ct-legal-intro-text">{t('legal.copy')}</p>
            </div>
            <section className="ct-footer-legal-grid" aria-label={t('legal.title')}>
              {[
                ['privacy', 'ph ph-shield-check', 'legal.privacyTitle', 'legal.privacyCopy'],
                ['terms', 'ph ph-file-text', 'legal.termsTitle', 'legal.termsCopy'],
                ['cookies', 'ph ph-hard-drives', 'legal.cookiesTitle', 'legal.cookiesCopy'],
                ['contact', 'ph ph-envelope-simple', 'legal.contactTitle', 'legal.contactCopy'],
              ].map(([key, icon, titleKey, copyKey]) => (
                <button type="button" key={key} onClick={() => setActiveLegal(key)}>
                  <i className={icon}></i>
                  <strong>{t(titleKey)}</strong>
                  <span>{t(copyKey)}</span>
                </button>
              ))}
            </section>
          </article>
        </main>
      );
    }
    return null;
  }

  function activeLegalTitle() {
    return t(footerLegalItems.find((item) => item.key === activeLegal)?.titleKey || 'app.legal');
  }

  if (loading) {
    return (
      <div className="ct-app-shell">
        <main className="ct-main">
          <div className="ct-card ct-empty">{t('common.loading')}</div>
        </main>
      </div>
    );
  }

  return (
    <div className="ct-app-shell">
      <header className="ct-topbar">
        <div className="ct-topbar-inner">
          <a href="/" className="ct-brand" onClick={goToFeed} aria-label="Outfit Detective">
            <span className="ct-brand-mark image">
              <img className="ct-brand-desktop-logo" src="/brand/outfit-detective-mark-transparent.svg" alt="" />
              <img className="ct-brand-mobile-logo" src="/brand/outfit-detective-mark-transparent.svg" alt="" />
            </span>
            <span>Outfit Detective</span>
            <em className="ct-beta-pill">{t('beta.badge')}</em>
          </a>
          <nav className="ct-social-tabs" aria-label="Primary">
            <a href="/" className={location.pathname === '/' ? 'active' : ''} onClick={goToFeed} title={t('app.feed')} aria-label={t('app.feed')}>
              <i className="ph-fill ph-house"></i>
            </a>
            <NavLink to="/fit-wall" title={t('app.fitWall')} aria-label={t('app.fitWall')}>
              <i className="ph ph-sparkle"></i>
            </NavLink>
            <NavLink
              to="/photos"
              title={t('app.photos')}
              aria-label={t('app.photos')}
              onClick={(event) => requireLoginFor(event, { title: t('authPrompt.photosTitle'), copy: t('authPrompt.photosCopy'), afterLogin: () => navigate('/photos') })}
            >
              <i className="ph ph-images-square"></i>
            </NavLink>
            <NavLink
              to="/discover"
              title={t('app.discover')}
              aria-label={t('app.discover')}
              onClick={(event) => requireLoginFor(event, { title: t('authPrompt.followTitle'), copy: t('authPrompt.followCopy'), afterLogin: () => navigate('/discover') })}
            >
              <i className="ph ph-magnifying-glass"></i>
            </NavLink>
            <NavLink
              to="/how-it-works"
              title={t('how.kicker')}
              aria-label={t('how.kicker')}
            >
              <i className="ph ph-question"></i>
            </NavLink>
          </nav>
          <div className="ct-user-menu">
            {isAuthenticated ? (
              <NavLink
                to="/profile?tab=alerts"
                className="ct-alert-trigger"
                aria-label={t('alerts.tab')}
                title={t('alerts.tab')}
              >
                <i className={unreadAlerts > 0 ? 'ph-fill ph-bell-ringing' : 'ph ph-bell'}></i>
                {unreadAlerts > 0 ? <span>{unreadAlerts > 9 ? '9+' : unreadAlerts}</span> : null}
              </NavLink>
            ) : null}
            <div className="ct-language-menu" ref={languageRef}>
              <button
                type="button"
                className="ct-language-trigger"
                onClick={() => setLanguageOpen((value) => !value)}
                aria-label={t('app.language')}
                aria-expanded={languageOpen}
              >
                <span aria-hidden="true">{currentLanguage.flag}</span>
                <i className="ph ph-caret-down"></i>
              </button>
              {languageOpen ? (
                <div className="ct-language-options" role="menu">
                  {languages.map((item) => (
                    <button
                      type="button"
                      key={item.code}
                      className={item.code === language ? 'active' : ''}
                      onClick={() => {
                        setLanguage(item.code);
                        setLanguageOpen(false);
                      }}
                      role="menuitem"
                    >
                      <span aria-hidden="true">{item.flag}</span>
                      <strong>{item.label}</strong>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            <div className="ct-account-menu" ref={userMenuRef}>
              <button
                type="button"
                className="ct-account-trigger"
                onClick={() => setUserMenuOpen((value) => !value)}
                aria-label={t('app.menu')}
                aria-expanded={userMenuOpen}
              >
                <i className="ph ph-dots-three-outline"></i>
              </button>
              {userMenuOpen ? (
                <div className="ct-account-options" role="menu">
                  {isAuthenticated ? (
                    <NavLink to="/profile" role="menuitem" onClick={() => setUserMenuOpen(false)}>
                      <i className="ph ph-user-circle"></i>
                      <span>{t('app.profile')}</span>
                    </NavLink>
                  ) : (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setUserMenuOpen(false);
                        requestLogin({ title: t('authPrompt.title'), copy: t('authPrompt.copy'), afterLogin: () => navigate('/profile') });
                      }}
                    >
                      <i className="ph ph-sign-in"></i>
                      <span>{t('authPrompt.signIn')}</span>
                    </button>
                  )}
                  <NavLink to="/how-it-works" role="menuitem" onClick={() => setUserMenuOpen(false)}>
                    <i className="ph ph-question"></i>
                    <span>{t('how.kicker')}</span>
                  </NavLink>
                  <NavLink to="/legal" role="menuitem" onClick={() => setUserMenuOpen(false)}>
                    <i className="ph ph-scales"></i>
                    <span>{t('app.legal')}</span>
                  </NavLink>
                  <button type="button" className="ct-mobile-menu-item" onClick={openMobileFollows} role="menuitem">
                    <i className="ph ph-list-heart"></i>
                    <span>{t('feed.yourList')}</span>
                  </button>
                  {isAuthenticated ? (
                    <button type="button" onClick={logout} role="menuitem">
                      <i className="ph ph-sign-out"></i>
                      <span>{t('app.logout')}</span>
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </header>
      <ShareToastHost />
      <main className="ct-main">
        <Outlet />
      </main>
      <footer className="ct-app-footer">
        <button type="button" className="ct-footer-brand" onClick={() => setActiveLegal('legal')} aria-label={t('app.legal')}>
          <img src="/brand/outfit-detective-logo-transparent.svg" alt="Outfit Detective" />
        </button>
        <div className="ct-footer-legal-links">
          <Link to="/how-it-works">{t('how.kicker')}</Link>
          {footerLegalItems.map((item) => (
            <button type="button" key={item.key} onClick={() => setActiveLegal(item.key)}>
              {t(item.titleKey)}
            </button>
          ))}
        </div>
      </footer>
      {activeLegal ? (
        <div className="ct-legal-modal-overlay" role="presentation" onClick={() => setActiveLegal('')}>
          <section
            className="ct-legal-modal ct-footer-legal-modal"
            role="dialog"
            aria-modal="true"
            aria-label={activeLegalTitle()}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="ct-legal-modal-bar">
              <span>{activeLegalTitle()}</span>
              <div className="ct-legal-modal-actions">
                <LegalLanguageMenu />
                <button type="button" onClick={() => setActiveLegal('')} aria-label={t('aiDialog.close')}>
                  <i className="ph ph-x"></i>
                </button>
              </div>
            </div>
            <div className="ct-legal-modal-content">
              {renderLegalDocument()}
            </div>
          </section>
        </div>
      ) : null}
      <nav className="ct-mobile-bottom-tabs" aria-label="Primary mobile">
        <a href="/" className={location.pathname === '/' ? 'active' : ''} onClick={goToFeed} title={t('app.feed')} aria-label={t('app.feed')}>
          <i className="ph-fill ph-house"></i>
          <span>{t('nav.feed')}</span>
        </a>
        <NavLink to="/fit-wall" title={t('app.fitWall')} aria-label={t('app.fitWall')}>
          <i className="ph ph-sparkle"></i>
          <span>{t('nav.fitWall')}</span>
        </NavLink>
        <NavLink
          to="/photos"
          title={t('app.photos')}
          aria-label={t('app.photos')}
          onClick={(event) => requireLoginFor(event, { title: t('authPrompt.photosTitle'), copy: t('authPrompt.photosCopy'), afterLogin: () => navigate('/photos') })}
        >
          <i className="ph ph-images-square"></i>
          <span>{t('nav.photos')}</span>
        </NavLink>
        <NavLink
          to="/discover"
          title={t('app.discover')}
          aria-label={t('app.discover')}
          onClick={(event) => requireLoginFor(event, { title: t('authPrompt.followTitle'), copy: t('authPrompt.followCopy'), afterLogin: () => navigate('/discover') })}
        >
          <i className="ph ph-magnifying-glass"></i>
          <span>{t('nav.discover')}</span>
        </NavLink>
        <NavLink
          to="/how-it-works"
          title={t('how.kicker')}
          aria-label={t('how.kicker')}
        >
          <i className="ph ph-question"></i>
          <span>{t('nav.help')}</span>
        </NavLink>
      </nav>
      <AuthPromptDialog onLegalOpen={setActiveLegal} />
      {followRefreshPrompt ? (
        <div className="ct-welcome-overlay" role="presentation" onClick={dismissFollowRefreshPrompt}>
          <section
            className="ct-follow-refresh-dialog"
            role="dialog"
            aria-modal="true"
            aria-label={t('followRefresh.title')}
            onClick={(event) => event.stopPropagation()}
          >
            <button type="button" className="ct-preview-close" onClick={dismissFollowRefreshPrompt} aria-label={t('followRefresh.dismiss')}>
              <i className="ph ph-x"></i>
            </button>
            <span className="ct-follow-refresh-icon" aria-hidden="true">
              <i className="ph ph-arrows-clockwise"></i>
            </span>
            <p className="ct-kicker">{t('followRefresh.kicker')}</p>
            <h2>{t('followRefresh.title')}</h2>
            <p>{t('followRefresh.copy')}</p>
            {followRefreshStatus ? <p className="ct-follow-refresh-status">{followRefreshStatus}</p> : null}
            <div className="ct-welcome-actions">
              <button type="button" onClick={refreshFollowedFeeds} disabled={followRefreshLoading}>
                {followRefreshLoading ? t('followRefresh.refreshing') : t('followRefresh.cta')}
              </button>
              <button type="button" onClick={dismissFollowRefreshPrompt}>
                {t('followRefresh.secondary')}
              </button>
            </div>
          </section>
        </div>
      ) : null}
      {mobileFollowsOpen ? (
        <div className="ct-mobile-follows-overlay" role="presentation" onClick={() => setMobileFollowsOpen(false)}>
          <section
            className="ct-mobile-follows-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ct-mobile-follows-title"
            onClick={(event) => event.stopPropagation()}
            onPointerDown={startMobileFollowsDrag}
            onPointerMove={moveMobileFollowsDrag}
            onPointerUp={endMobileFollowsDrag}
            onPointerCancel={endMobileFollowsDrag}
          >
            <div className="ct-sheet-drag-zone" aria-hidden="true">
              <span className="ct-sheet-handle"></span>
            </div>
            <div className="ct-panel-heading">
              <div>
                <p className="ct-kicker">{t('feed.following')}</p>
                <h2 id="ct-mobile-follows-title">{t('feed.yourList')}</h2>
              </div>
              <button type="button" className="ct-icon-link" onClick={() => setMobileFollowsOpen(false)} aria-label={t('aiDialog.close')}>
                <i className="ph ph-x"></i>
              </button>
            </div>
            {mobileFollowsError ? <div className="alert alert-danger">{mobileFollowsError}</div> : null}
            {mobileFollowsLoading ? (
              <div className="ct-card ct-empty">{t('common.loading')}</div>
            ) : mobileFollows.length === 0 ? (
              <div className="ct-rail-empty">
                <p>{t('feed.noProfiles')}</p>
                <Link to="/discover" className="btn btn-dark btn-sm" onClick={() => setMobileFollowsOpen(false)}>
                  {t('feed.findProfiles')}
                </Link>
              </div>
            ) : (
              <>
                <label className="ct-follow-search">
                  <i className="ph ph-magnifying-glass"></i>
                  <input
                    type="search"
                    value={mobileFollowsSearch}
                    onChange={(event) => setMobileFollowsSearch(event.target.value)}
                    placeholder={t('feed.searchList')}
                    aria-label={t('feed.searchList')}
                  />
                  {mobileFollowsSearch ? (
                    <button type="button" onClick={() => setMobileFollowsSearch('')} aria-label={t('common.cancel')}>
                      <i className="ph ph-x"></i>
                    </button>
                  ) : null}
                </label>
                {filteredMobileFollows.length === 0 ? (
                  <div className="ct-rail-empty compact">
                    <p>{t('feed.noListSearchResults')}</p>
                  </div>
                ) : (
                  <div className="ct-follow-list">
                    {filteredMobileFollows.map((item) => (
                      <div className="ct-follow-row" key={item.id}>
                        <Link to={`/following/${item.slug}`} className="ct-avatar small" onClick={() => setMobileFollowsOpen(false)}>
                          {item.image_url ? (
                            <img
                              src={avatarImageUrl(item.image_url)}
                              alt={item.name}
                              onError={(e) => {
                                e.currentTarget.replaceWith(document.createTextNode((item.name || '?').slice(0, 1).toUpperCase()));
                              }}
                            />
                          ) : (
                            (item.name || '?').slice(0, 1).toUpperCase()
                          )}
                        </Link>
                        <div>
                          <Link to={`/following/${item.slug}`} onClick={() => setMobileFollowsOpen(false)}>
                            <strong>{item.name}</strong>
                          </Link>
                        </div>
                        <button type="button" onClick={() => removeMobileFollow(item.id)} title={t('common.unfollow')}>
                          <i className="ph ph-x"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      ) : null}
      {showNewUserWelcome ? (
        <div className="ct-welcome-overlay" role="presentation" onClick={dismissNewUserWelcome}>
          <section
            className="ct-welcome-dialog"
            role="dialog"
            aria-modal="true"
            aria-label={t('welcome.title')}
            onClick={(event) => event.stopPropagation()}
          >
            <button type="button" className="ct-preview-close" onClick={dismissNewUserWelcome} aria-label={t('welcome.dismiss')}>
              <i className="ph ph-x"></i>
            </button>
            <p className="ct-kicker">{t('welcome.kicker')}</p>
            <h2>{t('welcome.title')}</h2>
            <p>{t('welcome.copy')}</p>
            <div className="ct-welcome-preferences" aria-label={t('welcome.preferencesLabel')}>
              {['budget', 'street', 'minimal', 'luxury', 'everyday'].map((key) => (
                <button
                  type="button"
                  key={key}
                  onClick={() => {
                    try {
                      const saved = JSON.parse(localStorage.getItem('od_style_preferences') || '[]');
                      const next = saved.includes(key) ? saved.filter((item) => item !== key) : [...saved, key];
                      localStorage.setItem('od_style_preferences', JSON.stringify(next));
                    } catch {
                      localStorage.setItem('od_style_preferences', JSON.stringify([key]));
                    }
                  }}
                >
                  {t(`welcome.pref.${key}`)}
                </button>
              ))}
            </div>
            <div className="ct-welcome-points">
              <span>
                <i className="ph ph-sparkle"></i>
                <strong>{t('welcome.pointFeedTitle')}</strong>
                <em>{t('welcome.pointFeedCopy')}</em>
              </span>
              <span>
                <i className="ph ph-binoculars"></i>
                <strong>{t('welcome.pointDetectTitle')}</strong>
                <em>{t('welcome.pointDetectCopy')}</em>
              </span>
              <span>
                <i className="ph ph-shopping-bag-open"></i>
                <strong>{t('welcome.pointBudgetTitle')}</strong>
                <em>{t('welcome.pointBudgetCopy')}</em>
              </span>
            </div>
            <div className="ct-welcome-actions">
              <NavLink to="/discover" onClick={dismissNewUserWelcome}>
                {t('welcome.cta')}
              </NavLink>
              <button type="button" onClick={dismissNewUserWelcome}>
                {t('welcome.secondary')}
              </button>
            </div>
            <p className="ct-welcome-legal">
              {t('welcome.legalPrefix')}{' '}
              <Link to="/privacy" onClick={dismissNewUserWelcome}>{t('login.legalLink')}</Link>{' '}
              {t('welcome.legalAnd')}{' '}
              <Link to="/terms" onClick={dismissNewUserWelcome}>{t('login.termsLink')}</Link>.
            </p>
          </section>
        </div>
      ) : null}
    </div>
  );
};

export default CelebLayout;
